// pages/GridDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { getToken, isAuthenticated } from '../services/authService';
import { getGridById } from '../services/gridService';
import { getMooringsByGridId } from '../services/mooringService';
import { getAllSensorPacks } from '../services/sensorPackService';
import { calculateDistance, getStatusColor, formatDate, formatCoordinates } from '../utils/utilities';
import '../styles/GridDetailPage.css';

const GOOGLE_MAP_API_TOKEN = process.env.REACT_APP_GOOGLE_MAP_API_TOKEN;

const GridDetailPage = () => {
  const { gridId } = useParams();
  const [grid, setGrid] = useState(null);
  const [moorings, setMoorings] = useState([]);
  const [sensorPackMap, setSensorPackMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredMooring, setHoveredMooring] = useState(null);

  useEffect(() => {
    // Only fetch data if user is authenticated
    if (!isAuthenticated()) return;

    const loadData = async () => {
      try {
        setLoading(true);
        // Get token from authService
        const token = getToken();

        // Extract gridId from the URL using window.location if useParams() isn't working
        let currentGridId = gridId;
        if (!currentGridId) {
          const pathSegments = window.location.pathname.split('/');
          const gridSegmentIndex = pathSegments.findIndex(segment => segment === 'grids');
          if (gridSegmentIndex !== -1 && gridSegmentIndex < pathSegments.length - 1) {
            currentGridId = pathSegments[gridSegmentIndex + 1];
          }
        }

        if (!currentGridId) {
          setError("Could not determine grid ID from URL");
          setLoading(false);
          return;
        }


        // Fetch grid, moorings, and all sensor packs data in parallel for efficiency
        const [gridData, mooringsData, allSensorPacks] = await Promise.all([
          getGridById(currentGridId, token),
          getMooringsByGridId(currentGridId, token),
          getAllSensorPacks(token)
        ]);

        // Create a mapping of device IDs to numeric IDs
        const packMap = {};

        // Process all the sensor packs to build our mapping
        // Handle the paginated data structure returned by the API
        if (allSensorPacks && allSensorPacks.results && Array.isArray(allSensorPacks.results)) {

          allSensorPacks.results.forEach(pack => {

            // Check for common field names that might contain the device ID
            // Use blues_device_id field based on the actual API response
            if (pack.blues_device_id && pack.id) {
              packMap[pack.blues_device_id] = pack.id;
            } else if (pack.device_id && pack.id) {
              packMap[pack.device_id] = pack.id;
            } else if (pack.device && pack.id) {
              packMap[pack.device] = pack.id;
            } else if (pack.identifier && pack.id) {
              packMap[pack.identifier] = pack.id;
            } else {
              // Log if we couldn't find mappable fields
              console.log("Could not map sensor pack - missing expected fields:", pack);
            }
          });
        } else if (Array.isArray(allSensorPacks)) {
          // Handle case where API might return a direct array
          allSensorPacks.forEach(pack => {
            if (pack.blues_device_id && pack.id) {
              packMap[pack.blues_device_id] = pack.id;
            } else if (pack.device_id && pack.id) {
              packMap[pack.device_id] = pack.id;
            } else if (pack.device && pack.id) {
              packMap[pack.device] = pack.id;
            } else if (pack.identifier && pack.id) {
              packMap[pack.identifier] = pack.id;
            }
          });
        }

        setGrid(gridData);
        setMoorings(mooringsData);
        setSensorPackMap(packMap);
      } catch (err) {
        console.error("API Error:", err);
        setError(`Failed to load data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [gridId]);

  // Redirect to login if not authenticated
  if (!isAuthenticated()) {
    return <Navigate to="/login" state={{ from: `/grids/${gridId}` }} />;
  }

  if (loading) return <div className="loading-container">Loading...</div>;
  if (error) return <div className="error-container">Error: {error}</div>;
  if (!grid) return <div className="error-container">Grid not found</div>;

  // Find center point for map
  const getMapCenter = () => {
    const validMoorings = moorings.filter(m =>
      m.status !== null && m.status.latitude !== null && m.status.longitude !== null
    );

    console.log("valid moorings: ", validMoorings);

    if (validMoorings.length === 0) {
      // Default to first mooring's target location if no current locations
      return {
        lat: moorings[0]?.target_latitude || 0,
        lng: moorings[0]?.target_longitude || 0
      };
    }

    // Calculate average of all valid locations
    const totalLat = validMoorings.reduce((sum, m) => sum + m.status.latitude, 0);
    const totalLng = validMoorings.reduce((sum, m) => sum + m.status.longitude, 0);

    return {
      lat: totalLat / validMoorings.length,
      lng: totalLng / validMoorings.length
    };
  };

  const mapCenter = getMapCenter();

  // Handle marker mouse events
  const handleMarkerMouseOver = (mooring) => {
    setHoveredMooring(mooring);
  };

  const handleMarkerMouseOut = () => {
    setHoveredMooring(null);
  };

  return (
    <div className="grid-detail-container">
      <header>
        <h1>{grid.name}</h1>
        <p>Location: {grid.city}, {grid.region}, {grid.country}</p>
        <p>Timezone: {grid.timezone}</p>
      </header>

      <section className="moorings-section">
        <h2>Moorings</h2>

        {moorings.length === 0 ? (
          <div className="no-moorings">No moorings found for this grid.</div>
        ) : (
          <ul className="moorings-list">
            {moorings.map(mooring => {
              const statusColor = mooring.status?.latest_sync
              ? getStatusColor(mooring.status.latest_sync)
              : null;
              const distance = mooring.status && mooring.target_latitude && mooring.target_longitude
              ? calculateDistance(
                  mooring.target_latitude,
                  mooring.target_longitude,
                  mooring.status.latitude,
                  mooring.status.longitude
                )
              : null;

              return (
                <li key={mooring.id} className="mooring-item">
                  {sensorPackMap[mooring.sensor_pack] ? (
                    // If we have the mapped ID, use it for the link
                    <Link
                      to={`/grids/${grid.id}/moorings/${mooring.id}`}
                      className="mooring-link"
                    >
                      <div className="mooring-header">
                        <span
                          className="status-indicator"
                          style={{ backgroundColor: statusColor }}
                        />
                        <h3>Buoy: {mooring.buoy}</h3>
                      </div>

                      <div className="mooring-details">
                      <p>Latest Sync: {mooring.status?.latest_sync ? formatDate(mooring.status.latest_sync) : 'N/A'}</p>
                      <p>Location: {mooring.status?.latitude && mooring.status?.longitude ? formatCoordinates(mooring.status.latitude, mooring.status.longitude) : 'N/A'}</p>
                        <p>
                          Distance from target: {distance !== null ?
                            `${distance} meters` :
                            'Cannot calculate (missing coordinates)'}
                        </p>
                        <p className="sensor-id">
                          Sensor Pack: {mooring.sensor_pack}
                          (ID: {sensorPackMap[mooring.sensor_pack]})
                        </p>
                      </div>
                    </Link>
                  ) : (
                    // If we don't have the ID mapping, render without a link
                    <div className="mooring-link">
                      <div className="mooring-header">
                        <span
                          className="status-indicator"
                          style={{ backgroundColor: statusColor }}
                        />
                        <h3>Buoy #{mooring.buoy}</h3>
                      </div>

                      <div className="mooring-details">
                        <p>Latest Sync: {formatDate(mooring.status.latest_sync)}</p>
                        <p>Location: {formatCoordinates(mooring.status.latitude, mooring.status.longitude)}</p>
                        <p>
                          Distance from target: {distance !== null ?
                            `${distance} meters` :
                            'Cannot calculate (missing coordinates)'}
                        </p>
                        <p className="sensor-id">
                          Sensor Pack: {mooring.sensor_pack}
                          <br/>
                          <span className="error-text">ID mapping not available - cannot navigate to sensor pack</span>
                        </p>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="map-section">
        <LoadScript googleMapsApiKey={GOOGLE_MAP_API_TOKEN}>
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '400px' }}
            center={mapCenter}
            zoom={14}
          >
            {moorings.map(mooring => {
              // Only add marker if we have valid coordinates
              if (mooring.status && mooring.status.latitude && mooring.status.longitude) {
                const statusColor = getStatusColor(mooring.status.latest_sync);
                return (
                  <React.Fragment key={mooring.id}>
                    <Marker
                      position={{
                        lat: mooring.status.latitude,
                        lng: mooring.status.longitude
                      }}
                      icon={{
                        path: "M0,0a8,8 0 1,0 16,0a8,8 0 1,0 -16,0",
                        fillColor: statusColor,
                        fillOpacity: 0.8,
                        strokeWeight: 1,
                        strokeColor: '#FFFFFF',
                        scale: 1
                      }}
                      title={`Buoy #${mooring.buoy}`}
                      onMouseOver={() => handleMarkerMouseOver(mooring)}
                      onMouseOut={handleMarkerMouseOut}
                    />

                    {/* Show InfoWindow when mooring is hovered */}
                    {hoveredMooring && hoveredMooring.id === mooring.id && (
                      <InfoWindow
                        position={{
                          lat: mooring.status.latitude,
                          lng: mooring.status.longitude
                        }}
                        onCloseClick={() => setHoveredMooring(null)}
                      >
                        <div className="info-window-content">
                          <h4>Buoy #{mooring.buoy}</h4>
                          <p>Latest Sync: {formatDate(mooring.status.latest_sync)}</p>
                          <p>Position: Lat {mooring.status.latitude}, Lon {mooring.status.longitude}</p>
                        </div>
                      </InfoWindow>
                    )}
                  </React.Fragment>
                );
              }
              return null;
            })}
          </GoogleMap>
        </LoadScript>
      </section>

      {/* Add some CSS for the InfoWindow content */}
      <style jsx>{`
        .info-window-content {
          padding: 5px;
          min-width: 150px;
        }

        .info-window-content h4 {
          margin: 0 0 5px 0;
          font-weight: bold;
        }

        .info-window-content p {
          margin: 3px 0;
          font-size: 12px;
        }
      `}</style>
    </div>
  );
};

export default GridDetailPage;